import {
  Activity,
  AlertCircle,
  ArrowRight,
  BarChart3,
  Building2,
  Calendar,
  CheckCircle2,
  ChevronDown,
  ChevronsUpDown,
  ChevronUp,
  Clock,
  FileText,
  Users,
} from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardSelector } from '@/components/dashboard';
import { DashboardFilter, type DashboardFilterOption } from '@/components/dashboard/dashboard-filter';
import { HelpSupportButton } from '@/components/help-support';
import { NavigationMetricCardAlt } from '@/components/NavigationMetricCardAlt';
import { getCurrencyIcon } from '@/components/shared/currency-icon';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Command, CommandGroup, CommandItem, CommandList } from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Progress } from '@/components/ui/progress';
import { ScrollArea, ScrollAreaViewport, ScrollBar } from '@/components/ui/scroll-area';
import { useCompany } from '@/contexts/CompanyContext';
import { useCanViewAllDisputes } from '@/hooks/use-disputes-metrics';
import type { DashboardMetrics } from '@/hooks/useDisputeDashboardData';
import { useDisputeDashboardData } from '@/hooks/useDisputeDashboardData';
import { formatCurrency, getCurrencySymbol } from '@/lib/utils';
import { getDerivedStatus, getTotalLevelsForDisputeType } from '@/lib/utils/dispute-workflow-utils';

const getPriorityBadgeVariant = (
  priority: string,
): {
  variant?: 'default' | 'secondary' | 'destructive' | 'outline' | 'warning';
  accent?:
    | 'teal'
    | 'purple'
    | 'info'
    | 'success'
    | 'warning'
    | 'destructive'
    | 'nile'
    | 'cerulean'
    | 'lagoon'
    | 'eagle'
    | 'sap'
    | 'orange'
    | 'brick'
    | 'red'
    | 'violet'
    | 'blue-violet';
} => {
  switch (priority) {
    case 'Critical':
      return { variant: 'destructive' };
    case 'High':
      return { variant: 'warning', accent: 'orange' };
    case 'Medium':
      return { variant: 'default' };
    case 'Low':
      return { variant: 'secondary' };
    default:
      return { variant: 'default' };
  }
};

const getPriorityBorderColor = (priority: string) => {
  switch (priority) {
    case 'Critical':
      return 'rgb(239, 68, 68)';
    case 'High':
      return 'rgb(249, 115, 22)';
    case 'Medium':
      return 'rgb(148, 163, 184)';
    case 'Low':
      return 'rgb(156, 163, 175)';
    default:
      return 'rgb(156, 163, 175)';
  }
};

const getStatusColor = (status: string) => {
  switch (status) {
    case 'New':
      return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300';
    case 'In Progress':
      return 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-300';
    case 'Resolved':
      return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300';
    case 'Closed':
      return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300';
    default:
      return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300';
  }
};

const DISPUTE_STATUS_OPTIONS = [
  { value: 'New', label: 'New' },
  { value: 'In Progress', label: 'In Progress' },
  { value: 'Escalated', label: 'Escalated' },
  { value: 'Resolved', label: 'Resolved' },
  { value: 'Closed', label: 'Closed' },
] as const;

const ACTIVE_STATUSES = ['New', 'In Progress', 'Escalated'];
const ALL_STATUSES = ['New', 'In Progress', 'Escalated', 'Resolved', 'Closed'];
const NO_DISPUTES_FOR_STATUSES = 'No disputes found for the selected statuses';

function isActiveOnlySelection(statuses: string[]): boolean {
  return statuses.length === ACTIVE_STATUSES.length && ACTIVE_STATUSES.every((x) => statuses.includes(x));
}

function formatStatusFilterLabel(statuses: string[]): string {
  if (statuses.length === 0) return 'no';
  if (statuses.length === ALL_STATUSES.length) return 'all';
  if (isActiveOnlySelection(statuses)) return 'active';
  const labels = statuses.map((x) => x.toLowerCase());
  return labels.length === 1 ? labels[0] : `${labels.slice(0, -1).join(', ')} & ${labels[labels.length - 1]}`;
}

interface DisputeStatusFilterProps {
  label: string;
  value: string[];
  onValueChange: (value: string[]) => void;
}

function DisputeStatusFilter({ label, value, onValueChange }: DisputeStatusFilterProps) {
  const [open, setOpen] = useState(false);

  const triggerLabel = isActiveOnlySelection(value) ? 'Active' : value.join(', ');

  const toggleStatus = (status: string) => {
    if (!value.includes(status)) {
      onValueChange(ALL_STATUSES.filter((x) => value.includes(x) || x === status));
      return;
    }
    // At least one status must always remain selected
    if (value.length === 1) return;
    onValueChange(value.filter((x) => x !== status));
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          aria-label={label}
          className="h-8 max-w-[240px] gap-1"
          onClick={(event) => event.stopPropagation()}
        >
          <span className="truncate">{triggerLabel}</span>
          <ChevronsUpDown className="h-3.5 w-3.5 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      {/* The card header toggles collapse on click, so filter clicks must not bubble up to it */}
      <PopoverContent className="w-52 p-0" align="end" onClick={(event) => event.stopPropagation()}>
        <Command>
          <CommandList>
            <CommandGroup>
              {DISPUTE_STATUS_OPTIONS.map((option) => (
                <CommandItem key={option.value} value={option.label} onSelect={() => toggleStatus(option.value)}>
                  <Checkbox checked={value.includes(option.value)} className="mr-2" />
                  <span>{option.label}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

// Helper to get secondary value for active disputes metric card
function getActiveDisputesSecondaryValue(dueSoonCount: number): string {
  if (dueSoonCount > 0) return `${dueSoonCount} due soon`;
  return 'All on track';
}

export default function DisputesDashboardPage() {
  const navigate = useNavigate();
  const canViewAll = useCanViewAllDisputes();
  const { selectedCompany } = useCompany();
  const currencySymbol = getCurrencySymbol(selectedCompany?.currency || 'GBP');

  const DEFAULT_FILTER: DashboardFilterOption = {
    id: 'everyone',
    name: 'Everyone',
    type: 'all',
  };
  const [selectedFilter, setSelectedFilter] = useState<DashboardFilterOption>(() => {
    try {
      const stored = localStorage.getItem('dashboardFilter');
      if (stored) {
        const parsed = JSON.parse(stored) as DashboardFilterOption;
        if (parsed?.id && parsed?.name && parsed?.type) {
          return parsed;
        }
      }
    } catch {
      // Ignore malformed stored value
    }
    return DEFAULT_FILTER;
  });

  const filterUserName = selectedFilter.id === 'everyone' ? 'Team' : selectedFilter.name;
  const isFiltered = selectedFilter.id !== 'everyone';
  const selectedUserId = selectedFilter.id !== 'everyone' ? Number(selectedFilter.id) : undefined;

  // Independent status scopes for the two analytical cards — session-only, reset on refresh
  const [priorityStatusFilter, setPriorityStatusFilter] = useState<string[]>(ACTIVE_STATUSES);
  const [typesStatusFilter, setTypesStatusFilter] = useState<string[]>(ACTIVE_STATUSES);

  // Use the mode-aware dashboard data hook
  const { data: dashboardData, isLoading } = useDisputeDashboardData({
    assignedToUserId: selectedUserId,
    filterName: selectedFilter.name !== 'Everyone' ? selectedFilter.name : undefined,
    priorityStatusFilter,
    typesStatusFilter,
  });

  const defaultMetrics: DashboardMetrics = {
    activeCount: 0,
    totalAmount: 0,
    avgResolutionTime: 0,
    resolutionRate: 0,
    byPriority: {
      critical: { count: 0, amount: 0 },
      high: { count: 0, amount: 0 },
      medium: { count: 0, amount: 0 },
      low: { count: 0, amount: 0 },
    },
    byReason: [],
    recentDisputes: [],
    overdueCount: 0,
    dueSoonCount: 0,
    ageingBuckets: { '0-7': 0, '8-14': 0, '15-30': 0, '31+': 0 },
    avgWorkflowProgress: 0,
    topAssignees: [],
  };
  const metrics = dashboardData ?? defaultMetrics;

  // State for collapsible sections
  const [collapsedSections, setCollapsedSections] = useState<Record<string, boolean>>({
    priorityDisputes: false,
    teamWorkload: false,
    disputesByPriority: false,
    commonTypes: false,
  });

  const toggleSection = (section: string) => {
    setCollapsedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const priorityCardTotal =
    metrics.byPriority.critical.count +
    metrics.byPriority.high.count +
    metrics.byPriority.medium.count +
    metrics.byPriority.low.count;

  // Extracted card components for reuse in different layout positions
  const disputesByPriorityCard = (
    <Card>
      <CardHeader className="cursor-pointer" onClick={() => toggleSection('disputesByPriority')}>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-lg font-semibold">Disputes by Priority</CardTitle>
            <CardDescription>
              Distribution across priority levels — {formatStatusFilterLabel(priorityStatusFilter)} disputes
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <DisputeStatusFilter
              label="Filter disputes by priority statuses"
              value={priorityStatusFilter}
              onValueChange={setPriorityStatusFilter}
            />
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
              {collapsedSections.disputesByPriority ? (
                <ChevronDown className="h-4 w-4" />
              ) : (
                <ChevronUp className="h-4 w-4" />
              )}
            </Button>
          </div>
        </div>
      </CardHeader>
      {!collapsedSections.disputesByPriority && (
        <CardContent>
          {priorityCardTotal === 0 ? (
            <div className="text-center py-8 text-muted-foreground">{NO_DISPUTES_FOR_STATUSES}</div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Badge variant="destructive">Critical</Badge>
                  <span className="text-sm text-muted-foreground">{metrics.byPriority.critical.count} disputes</span>
                </div>
                <span className="text-sm font-medium">
                  {currencySymbol}
                  {(metrics.byPriority.critical.amount / 1000).toFixed(1)}K
                </span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Badge variant="warning" accent="orange">
                    High
                  </Badge>
                  <span className="text-sm text-muted-foreground">{metrics.byPriority.high.count} disputes</span>
                </div>
                <span className="text-sm font-medium">
                  {currencySymbol}
                  {(metrics.byPriority.high.amount / 1000).toFixed(1)}K
                </span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Badge variant="default">Medium</Badge>
                  <span className="text-sm text-muted-foreground">{metrics.byPriority.medium.count} disputes</span>
                </div>
                <span className="text-sm font-medium">
                  {currencySymbol}
                  {(metrics.byPriority.medium.amount / 1000).toFixed(1)}K
                </span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Badge variant="secondary">Low</Badge>
                  <span className="text-sm text-muted-foreground">{metrics.byPriority.low.count} disputes</span>
                </div>
                <span className="text-sm font-medium">
                  {currencySymbol}
                  {(metrics.byPriority.low.amount / 1000).toFixed(1)}K
                </span>
              </div>
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );

  const commonDisputeTypesCard = (
    <Card>
      <CardHeader className="cursor-pointer" onClick={() => toggleSection('commonTypes')}>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-lg font-semibold">Common Dispute Types</CardTitle>
            <CardDescription>
              Top dispute categories — {formatStatusFilterLabel(typesStatusFilter)} disputes
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <DisputeStatusFilter
              label="Filter common dispute types statuses"
              value={typesStatusFilter}
              onValueChange={setTypesStatusFilter}
            />
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
              {collapsedSections.commonTypes ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />}
            </Button>
          </div>
        </div>
      </CardHeader>
      {!collapsedSections.commonTypes && (
        <CardContent>
          {metrics.byReason.length > 0 ? (
            <div className="space-y-4">
              {metrics.byReason.map((item) => (
                <div key={item.reason} className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="font-medium">{item.reason}</span>
                    <span className="text-muted-foreground">{item.count}</span>
                  </div>
                  <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                    <div className="h-full bg-teal-600 rounded-full" style={{ width: `${item.percentage}%` }} />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">{NO_DISPUTES_FOR_STATUSES}</div>
          )}
        </CardContent>
      )}
    </Card>
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading dispute data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6 p-6">
      {/* Dashboard Toggle Buttons */}
      <div className="flex items-center justify-between">
        <DashboardSelector />

        <DashboardFilter selectedFilter={selectedFilter} onFilterChange={setSelectedFilter} />
      </div>

      {/* Header Section */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <h1 className="text-3xl font-bold tracking-tight">Dispute Dashboard</h1>
          <p className="text-muted-foreground">
            {isFiltered
              ? `Monitor ${filterUserName}'s dispute metrics and track resolution progress`
              : 'Monitor team dispute metrics and track resolution progress'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={() => navigate('/disputes/all')} className="bg-teal-600 hover:bg-teal-700">
            View All Disputes
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
          <HelpSupportButton screenId="disputes-dashboard" />
        </div>
      </div>

      {/* Key Metrics Cards Row */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <NavigationMetricCardAlt
          title="Active Disputes"
          value={metrics.activeCount.toString()}
          secondaryValue={
            metrics.overdueCount > 0
              ? `${metrics.overdueCount} overdue`
              : getActiveDisputesSecondaryValue(metrics.dueSoonCount)
          }
          icon={AlertCircle}
          variant="info"
          onClick={() => {
            // Navigate based on what's most urgent
            if (metrics.overdueCount > 0) {
              navigate('/disputes/all?overdue=true');
            } else {
              navigate('/disputes/all');
            }
          }}
        />
        <NavigationMetricCardAlt
          title="Amount in Dispute"
          value={`${currencySymbol}${(metrics.totalAmount / 1000).toFixed(1)}K`}
          secondaryValue={`Across ${metrics.activeCount} active dispute${metrics.activeCount !== 1 ? 's' : ''}`}
          icon={getCurrencyIcon(selectedCompany?.currency)}
          variant="info"
          onClick={() => navigate('/disputes/all')}
        />
        <NavigationMetricCardAlt
          title="Avg Resolution Time"
          value={metrics.avgResolutionTime > 0 ? `${metrics.avgResolutionTime} days` : 'N/A'}
          secondaryValue="Based on resolved disputes"
          icon={Clock}
          variant="info"
          onClick={() => navigate('/disputes/all?status=Resolved')}
        />
        <NavigationMetricCardAlt
          title="Resolution Rate"
          value={`${metrics.resolutionRate}%`}
          secondaryValue="Overall success rate"
          icon={CheckCircle2}
          variant="info"
          onClick={() => navigate('/disputes/all?status=Resolved')}
        />
      </div>

      {/* Workflow & Ageing Insights Row */}
      <div className="grid gap-4 md:grid-cols-2">
        {/* Workflow Progress */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Avg Workflow Progress</CardTitle>
            <Activity className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-2xl font-bold">{metrics.avgWorkflowProgress}%</span>
                <Badge variant="outline" className="text-xs">
                  {metrics.activeCount} active
                </Badge>
              </div>
              <Progress value={metrics.avgWorkflowProgress} className="h-2" />
              <p className="text-xs text-muted-foreground">Average completion across all workflows</p>
            </div>
          </CardContent>
        </Card>

        {/* Ageing Analysis */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Dispute Ageing</CardTitle>
            <BarChart3 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="flex justify-between text-xs">
                <span className="text-muted-foreground">0-7 days</span>
                <span className="font-medium">{metrics.ageingBuckets['0-7']}</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-muted-foreground">8-14 days</span>
                <span className="font-medium">{metrics.ageingBuckets['8-14']}</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-muted-foreground">15-30 days</span>
                <span className="font-medium">{metrics.ageingBuckets['15-30']}</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-red-600 font-medium">31+ days</span>
                <span className="font-bold text-red-600">{metrics.ageingBuckets['31+']}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Priority Disputes & Team Workload Row */}
      <div className="grid gap-4 md:grid-cols-2">
        {/* Priority Disputes Requiring Attention */}
        <Card>
          <CardHeader className="cursor-pointer" onClick={() => toggleSection('priorityDisputes')}>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-lg font-semibold">Priority Disputes Requiring Attention</CardTitle>
                <CardDescription>
                  {metrics.recentDisputes.length > 0
                    ? `Top ${metrics.recentDisputes.length} highest priority disputes`
                    : 'No active disputes requiring attention'}
                </CardDescription>
              </div>
              <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                {collapsedSections.priorityDisputes ? (
                  <ChevronDown className="h-4 w-4" />
                ) : (
                  <ChevronUp className="h-4 w-4" />
                )}
              </Button>
            </div>
          </CardHeader>
          {!collapsedSections.priorityDisputes && (
            <CardContent>
              {metrics.recentDisputes.length > 0 ? (
                <ScrollArea className="h-[600px]">
                  <ScrollAreaViewport className="h-full">
                    <div className="space-y-2 pr-4">
                      {metrics.recentDisputes.map((dispute) => (
                        <Card
                          key={dispute.id}
                          className="border-l-4 cursor-pointer hover:shadow-md transition-shadow"
                          style={{
                            borderLeftColor: getPriorityBorderColor(dispute.priority),
                          }}
                          onClick={() => navigate(`/disputes/all/${dispute.id}`)}
                        >
                          <CardContent className="py-3 px-4">
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-semibold text-sm">{dispute.disputeNumber}</span>
                                <Badge {...getPriorityBadgeVariant(dispute.priority)} className="text-xs py-0 h-5">
                                  {dispute.priority}
                                </Badge>
                                {(() => {
                                  const derivedStatus = getDerivedStatus(
                                    dispute.currentLevel,
                                    getTotalLevelsForDisputeType(dispute.type),
                                    dispute.closedReason,
                                    dispute.status,
                                  );
                                  return (
                                    <Badge className={`${getStatusColor(derivedStatus)} text-xs py-0 h-5`}>
                                      {derivedStatus}
                                    </Badge>
                                  );
                                })()}
                              </div>
                              <span className="text-base font-bold">
                                {formatCurrency(
                                  dispute.amount,
                                  dispute.currencyCode || selectedCompany?.currency || 'GBP',
                                )}
                              </span>
                            </div>
                            <div className="space-y-1.5">
                              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                <Building2 className="h-3.5 w-3.5" />
                                <span>{dispute.customer}</span>
                              </div>
                              <div className="flex items-center justify-between text-xs">
                                <div className="flex items-center gap-2 text-muted-foreground">
                                  <FileText className="h-3.5 w-3.5" />
                                  <span>{dispute.type}</span>
                                </div>
                                <div className="flex items-center gap-1 text-muted-foreground">
                                  <Calendar className="h-3.5 w-3.5" />
                                  <span>{dispute.daysOpen} days open</span>
                                </div>
                              </div>
                              {dispute.totalLevels > 0 && (
                                <div className="mt-1.5">
                                  <div className="flex items-center justify-between text-xs mb-1">
                                    <span className="text-muted-foreground">Workflow Progress</span>
                                    <span className="font-medium">
                                      Level {dispute.currentLevel} of {dispute.totalLevels}
                                    </span>
                                  </div>
                                  <Progress
                                    value={(dispute.currentLevel / dispute.totalLevels) * 100}
                                    className="h-1"
                                  />
                                </div>
                              )}
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </ScrollAreaViewport>
                  <ScrollBar orientation="vertical" />
                </ScrollArea>
              ) : (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <CheckCircle2 className="h-12 w-12 text-green-600 mb-4" />
                  <p className="text-lg font-medium">All Clear!</p>
                  <p className="text-sm text-muted-foreground mt-1">No disputes requiring immediate attention.</p>
                </div>
              )}
            </CardContent>
          )}
        </Card>

        {/* Team Workload Distribution - Only show for managers */}
        {canViewAll && !isFiltered ? (
          <Card>
            <CardHeader className="cursor-pointer" onClick={() => toggleSection('teamWorkload')}>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg font-semibold">Team Workload Distribution</CardTitle>
                  <CardDescription>Active disputes by assignee</CardDescription>
                </div>
                <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                  {collapsedSections.teamWorkload ? (
                    <ChevronDown className="h-4 w-4" />
                  ) : (
                    <ChevronUp className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </CardHeader>
            {!collapsedSections.teamWorkload && (
              <CardContent>
                {metrics.topAssignees.length > 0 ? (
                  <ScrollArea className="h-[600px]">
                    <ScrollAreaViewport className="h-full">
                      <div className="space-y-4 pr-4">
                        {metrics.topAssignees.map((assignee) => (
                          <div key={assignee.name} className="flex items-center justify-between">
                            <div className="flex items-center gap-3 flex-1">
                              <Users className="h-4 w-4 text-muted-foreground" />
                              <span className="font-medium">{assignee.name}</span>
                              <Badge variant="outline" className="text-xs">
                                {assignee.count} dispute
                                {assignee.count !== 1 ? 's' : ''}
                              </Badge>
                            </div>
                            <span className="text-sm font-medium">
                              {currencySymbol}
                              {(assignee.amount / 1000).toFixed(1)}K
                            </span>
                          </div>
                        ))}
                      </div>
                    </ScrollAreaViewport>
                    <ScrollBar orientation="vertical" />
                  </ScrollArea>
                ) : (
                  <div className="flex flex-col items-center justify-center py-12 text-center">
                    <Users className="h-12 w-12 text-muted-foreground mb-4" />
                    <p className="text-lg font-medium">No Active Assignments</p>
                    <p className="text-sm text-muted-foreground mt-1">No active disputes assigned to team members</p>
                  </div>
                )}
              </CardContent>
            )}
          </Card>
        ) : (
          <div className="flex flex-col gap-4">
            {disputesByPriorityCard}
            {commonDisputeTypesCard}
          </div>
        )}
      </div>

      {/* Disputes by Priority & Common Dispute Types Row - only shown when Team Workload is visible */}
      {canViewAll && !isFiltered && (
        <div className="grid gap-4 md:grid-cols-2">
          {disputesByPriorityCard}
          {commonDisputeTypesCard}
        </div>
      )}
    </div>
  );
}
